import React, { useContext, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import API from '../../services/API'
import { TenantSettingsContext } from '../../App'

import Alert from '@mui/material/Alert'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import LinearProgress from '@mui/material/LinearProgress'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import DownloadIcon from '@mui/icons-material/Download'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { DataGrid } from '@mui/x-data-grid'
import { InputAdornment } from '@mui/material'
import { getCurrencySymbol } from '../../helpers/currency'
import DocumentsField from '../../helpers/DocumentsField'
import { fileRecord, uploadDocument } from '../../helpers/uploadDocument'
import useDocumentPreview from '../../components/Documents/useDocumentPreview'
import {
  InvoiceRowDialog,
  OvertimeChip,
  buildInvoiceRows,
  invoiceGridSx,
  invoiceRowClassName,
  invoiceRowColumns,
  isInvoiceRowSelectable,
  normaliseInvoiceSelection,
  overtimePendingRowSx,
  pendingOvertimeRowClassName,
  selectionLabel,
  invoiceTotalsWithVat,
  selectionTotal,
  splitInvoiceSelection,
} from '../../helpers/staffInvoiceRows'

const expenseCategories = [
  'Travel',
  'Mileage',
  'Accommodation',
  'Equipment',
  'Subsistence',
  'Other',
]

const currencyCodeFromSymbol = (symbol) => (symbol === '€' ? 'EUR' : 'GBP')

const escapeCsv = (value) => {
  const stringValue = value === undefined || value === null ? '' : String(value)
  return `"${stringValue.replace(/"/g, '""')}"`
}

const MyPay = () => {
  const { orgSettings } = useContext(TenantSettingsContext).orgConfig
  const [statements, setStatements] = useState([])
  const [summary, setSummary] = useState({})
  const [pendingPay, setPendingPay] = useState({ lines: [], summary: {} })
  const [staffInvoices, setStaffInvoices] = useState([])
  const [workerPaymentType, setWorkerPaymentType] = useState('Unknown')
  // VAT-registered workers invoice their net pay plus standard-rate VAT
  const [invoiceVat, setInvoiceVat] = useState(null)
  const [selectedStatement, setSelectedStatement] = useState(null)
  const [uploadInvoiceOpen, setUploadInvoiceOpen] = useState(false)
  const [invoiceSelection, setInvoiceSelection] = useState([])
  const [invoiceForm, setInvoiceForm] = useState({
    invoiceNumber: '',
    invoiceDate: moment().format('YYYY-MM-DD'),
    amount: '',
    notes: '',
  })
  const [invoiceFiles, setInvoiceFiles] = useState([])
  const [invoiceError, setInvoiceError] = useState(null)
  const [viewedInvoiceRow, setViewedInvoiceRow] = useState(null)
  const [uploadingInvoice, setUploadingInvoice] = useState(false)
  const [generateInvoiceOpen, setGenerateInvoiceOpen] = useState(false)
  const [generateSelection, setGenerateSelection] = useState([])
  const [generateForm, setGenerateForm] = useState({
    invoiceNumber: '',
    invoiceDate: moment().format('YYYY-MM-DD'),
    notes: '',
  })
  const [generatingInvoice, setGeneratingInvoice] = useState(false)
  const [generateError, setGenerateError] = useState(null)
  const [expenses, setExpenses] = useState([])
  const [logExpenseOpen, setLogExpenseOpen] = useState(false)
  const [expenseForm, setExpenseForm] = useState({
    description: '',
    category: 'Other',
    amount: '',
    expenseDate: moment().format('YYYY-MM-DD'),
    shift: null,
    notes: '',
    receipt: null,
  })
  const [submittingExpense, setSubmittingExpense] = useState(false)
  const [expenseError, setExpenseError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [fetchError, setFetchError] = useState(false)

  const currency = useMemo(
    () =>
      new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: currencyCodeFromSymbol(orgSettings?.currency),
        minimumFractionDigits: 2,
      }),
    [orgSettings?.currency],
  )

  const loadPay = () => {
    setLoading(true)
    setFetchError(false)
    API.get('/my-pay')
      .then((res) => {
        if (res.status === 200) {
          setStatements(res.data.statements ?? [])
          setSummary(res.data.summary ?? {})
          setPendingPay(res.data.pendingPay ?? { lines: [], summary: {} })
          setStaffInvoices(res.data.staffInvoices ?? [])
          setExpenses(res.data.expenses ?? [])
          setWorkerPaymentType(res.data.workerPaymentType ?? 'Unknown')
          setInvoiceVat(res.data.invoiceVat ?? null)
        } else {
          setFetchError(true)
        }
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadPay()
  }, [])

  const downloadStatement = (statement) => {
    const rows = [
      [
        'Pay Run',
        'Statement Type',
        'Shift Date',
        'Description',
        'Role',
        'Hours',
        'Rate',
        'Base Pay',
        'Overtime Pay',
        'Bonus Pay',
        'Travel Pay',
        'Total Pay',
      ],
      ...(statement?.lines ?? []).map((line) => [
        statement.reference,
        statement.statementType,
        moment(line.start).format('YYYY-MM-DD HH:mm'),
        line.description,
        line.clinicalRole,
        line.hours,
        line.payRate,
        line.basePay,
        line.overtimePay,
        line.bonusPay ?? 0,
        line.travelPay ?? 0,
        line.totalPay,
      ]),
    ]

    const csv = rows.map((row) => row.map(escapeCsv).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${statement.reference}-statement.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  const { openPreview, previewModal } = useDocumentPreview()

  const submitExpense = async () => {
    const amount = Number(expenseForm.amount)
    const hasAmount = expenseForm.amount !== '' && Number.isFinite(amount)

    if (
      !expenseForm.description.trim() ||
      !hasAmount ||
      !expenseForm.expenseDate
    ) {
      return
    }

    setSubmittingExpense(true)
    setExpenseError(null)

    try {
      const file = expenseForm.receipt
      const key = file
        ? await uploadDocument(file, 'payroll/expenses').catch(() => null)
        : null
      if (file && !key) {
        throw new Error(
          'Unable to upload the receipt. Check your connection and try again.',
        )
      }

      const created = await API.post('/my-pay/expenses', {
        description: expenseForm.description,
        category: expenseForm.category,
        amount: expenseForm.amount,
        expenseDate: expenseForm.expenseDate,
        shift: expenseForm.shift?.shift ?? null,
        notes: expenseForm.notes,
        ...(key ? { receipt: fileRecord(file, key) } : {}),
      })

      // 4xx payloads carry a readable `error`; 5xx is handled globally
      if (created.status !== 200) {
        throw new Error(
          created.data?.error ||
            'Unable to submit this expense. Please try again.',
        )
      }

      setLogExpenseOpen(false)
      setExpenseForm({
        description: '',
        category: 'Other',
        amount: '',
        expenseDate: moment().format('YYYY-MM-DD'),
        shift: null,
        notes: '',
        receipt: null,
      })
      loadPay()
    } catch (err) {
      console.error('Unable to submit expense', err)
      setExpenseError(err.message)
    } finally {
      setSubmittingExpense(false)
    }
  }

  const closeUploadInvoice = () => {
    setUploadInvoiceOpen(false)
    setInvoiceError(null)
  }

  const uploadStaffInvoice = async () => {
    const uploadAmount = Number(invoiceForm.amount)
    const hasUploadAmount =
      invoiceForm.amount !== '' && Number.isFinite(uploadAmount)

    if (!invoiceSelection.length || !hasUploadAmount || invoiceAmountMismatch) {
      return
    }

    setUploadingInvoice(true)
    setInvoiceError(null)

    try {
      const file = invoiceFiles[0]
      const key = file
        ? await uploadDocument(file, 'payroll/staff-invoices').catch(() => null)
        : null
      if (file && !key) {
        throw new Error(
          'Unable to upload the invoice file. Check your connection and try again.',
        )
      }

      const created = await API.post('/my-pay/staff-invoices', {
        ...splitInvoiceSelection(invoiceSelection),
        invoiceNumber: invoiceForm.invoiceNumber,
        invoiceDate: invoiceForm.invoiceDate,
        amount: invoiceForm.amount,
        notes: invoiceForm.notes,
        ...(key ? { file: fileRecord(file, key) } : {}),
      })

      // 4xx payloads carry a readable `error`; 5xx is handled globally
      if (created.status !== 200) {
        throw new Error(
          created.data?.error ||
            'Unable to submit this invoice. Please try again.',
        )
      }

      setUploadInvoiceOpen(false)
      setInvoiceSelection([])
      setInvoiceFiles([])
      setInvoiceForm({
        invoiceNumber: '',
        invoiceDate: moment().format('YYYY-MM-DD'),
        amount: '',
        notes: '',
      })
      loadPay()
    } catch (err) {
      console.error('Unable to submit staff invoice', err)
      setInvoiceError(err.message)
    } finally {
      setUploadingInvoice(false)
    }
  }

  const generateStaffInvoice = async () => {
    if (!generateSelection.length) return

    setGeneratingInvoice(true)
    setGenerateError(null)

    try {
      const created = await API.post('/my-pay/staff-invoices/generate', {
        ...splitInvoiceSelection(generateSelection),
        invoiceNumber: generateForm.invoiceNumber,
        invoiceDate: generateForm.invoiceDate,
        notes: generateForm.notes,
      })

      // 4xx payloads carry a readable `error`; 5xx is handled globally
      if (created.status !== 200) {
        throw new Error(
          created.data?.error ||
            'Unable to generate this invoice. Please try again.',
        )
      }

      setGenerateInvoiceOpen(false)
      setGenerateSelection([])
      setGenerateForm({
        invoiceNumber: '',
        invoiceDate: moment().format('YYYY-MM-DD'),
        notes: '',
      })
      loadPay()
    } catch (err) {
      console.error('Unable to generate staff invoice', err)
      setGenerateError(err.message)
    } finally {
      setGeneratingInvoice(false)
    }
  }

  // Shifts in a draft pay run are locked; anything already on a live invoice
  // is dropped row by row (base pay and overtime separately)
  const invoiceRows = useMemo(
    () =>
      buildInvoiceRows(
        (pendingPay?.lines ?? []).filter((line) => !line.payRun),
      ),
    [pendingPay],
  )
  const canInvoice = invoiceRows.some((row) => row.selectable)
  const vatRate = invoiceVat?.registered ? invoiceVat.rate : 0
  // Row amounts are net; a VAT-registered worker invoices net + VAT
  const selectedInvoiceTotals = invoiceTotalsWithVat(
    selectionTotal(invoiceRows, invoiceSelection),
    vatRate,
  )
  const invoiceAmount = Number(invoiceForm.amount)
  const hasInvoiceAmount =
    invoiceForm.amount !== '' && Number.isFinite(invoiceAmount)
  // The invoice must match the selected total exactly (to the penny)
  const invoiceAmountMismatch =
    hasInvoiceAmount &&
    Math.abs(invoiceAmount - selectedInvoiceTotals.gross) > 0.005
  const selectedGenerateTotals = invoiceTotalsWithVat(
    selectionTotal(invoiceRows, generateSelection),
    vatRate,
  )

  const changeInvoiceSelection = (selection) => {
    const next = normaliseInvoiceSelection(invoiceRows, Array.from(selection))
    setInvoiceSelection(next)
    // Keep the amount in step with the selection; it can still be edited
    setInvoiceForm((prev) => ({
      ...prev,
      amount: next.length
        ? invoiceTotalsWithVat(
            selectionTotal(invoiceRows, next),
            vatRate,
          ).gross.toFixed(2)
        : '',
    }))
  }

  const changeGenerateSelection = (selection) =>
    setGenerateSelection(
      normaliseInvoiceSelection(invoiceRows, Array.from(selection)),
    )

  const recentShiftOptions = useMemo(() => {
    const cutoff = moment().subtract(90, 'days')
    const fromPending = (pendingPay?.lines ?? []).map((line) => ({
      shift: line.shift,
      start: line.start,
      description: line.description,
    }))
    const fromStatements = statements.flatMap((statement) =>
      (statement.lines ?? []).map((line) => ({
        shift: line.shift,
        start: line.start,
        description: line.description,
      })),
    )
    const seen = new Set()
    return [...fromPending, ...fromStatements].filter((line) => {
      if (!line.shift || seen.has(line.shift)) return false
      if (line.start && moment(line.start).isBefore(cutoff)) return false
      seen.add(line.shift)
      return true
    })
  }, [pendingPay, statements])

  return (
    <Stack gap={2}>
      <Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          gap={1}
        >
          <Stack>
            <Typography variant="title1">My Pay</Typography>
            <Typography variant="body2" color="text.secondary">
              Paid shift statements and remittance summaries.
            </Typography>
          </Stack>
          {workerPaymentType === 'Invoice' && (
            <Stack direction="row" gap={1}>
              <Button
                startIcon={<ReceiptLongIcon />}
                variant="outlined"
                onClick={() => setGenerateInvoiceOpen(true)}
                disabled={!canInvoice}
              >
                Generate Invoice
              </Button>
              <Button
                startIcon={<UploadFileIcon />}
                variant="contained"
                onClick={() => setUploadInvoiceOpen(true)}
                disabled={!canInvoice}
              >
                Upload Invoice
              </Button>
            </Stack>
          )}
        </Stack>
      </Stack>

      {fetchError && (
        <Alert severity="error">Unable to fetch your pay statements.</Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            lg: '1.2fr 1.2fr 0.8fr 1fr',
          },
          gap: 1,
          alignItems: 'stretch',
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            borderRadius: 2,
            minHeight: 70,
            display: 'flex',
            gap: 1,
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ lineHeight: 1.25 }}
          >
            Paid Total
          </Typography>
          <Typography variant="title2" sx={{ lineHeight: 1.15 }}>
            {currency.format(summary.totalPay ?? 0)}
          </Typography>
        </Paper>

        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            borderRadius: 2,
            minHeight: 70,
            gap: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ lineHeight: 1.25 }}
          >
            Pending Estimate
          </Typography>
          <Typography variant="title2" sx={{ lineHeight: 1.15 }}>
            {currency.format(pendingPay?.summary?.totalPay ?? 0)}
          </Typography>
        </Paper>

        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            borderRadius: 2,
            minHeight: 70,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            justifyContent: 'space-between',
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ lineHeight: 1.25 }}
          >
            Paid Activity
          </Typography>
          <Stack gap={0.75}>
            <Stack direction="row" justifyContent="space-between" gap={1}>
              <Typography variant="body2" color="text.secondary">
                Shifts
              </Typography>
              <Typography variant="body2" fontWeight={700}>
                {summary.shifts ?? 0}
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between" gap={1}>
              <Typography variant="body2" color="text.secondary">
                Hours
              </Typography>
              <Typography variant="body2" fontWeight={700}>
                {summary.hours ?? 0}
              </Typography>
            </Stack>
          </Stack>
        </Paper>

        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            borderRadius: 2,
            minHeight: 70,
            display: 'flex',
            gap: 1,
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ lineHeight: 1.25 }}
          >
            Paid By Type
          </Typography>
          <Stack gap={0.75}>
            <Stack direction="row" justifyContent="space-between" gap={1}>
              <Typography variant="body2" color="text.secondary">
                PAYE
              </Typography>
              <Typography variant="body2" fontWeight={700}>
                {currency.format(summary.paye ?? 0)}
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between" gap={1}>
              <Typography variant="body2" color="text.secondary">
                Invoice
              </Typography>
              <Typography variant="body2" fontWeight={700}>
                {currency.format(summary.invoice ?? 0)}
              </Typography>
            </Stack>
          </Stack>
        </Paper>
      </Box>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Stack gap={2}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            gap={1}
          >
            <Stack>
              <Typography variant="title2">Pending Pay</Typography>
              <Typography variant="body2" color="text.secondary">
                Estimated pay for completed shifts that have not been marked
                paid.
              </Typography>
            </Stack>
            <Stack direction="row" gap={1} flexWrap="wrap">
              <Chip
                label={`${pendingPay?.summary?.shifts ?? 0} shifts`}
                size="small"
                sx={{ borderRadius: 1 }}
              />
              <Chip
                label={currency.format(pendingPay?.summary?.totalPay ?? 0)}
                color="warning"
                size="small"
                sx={{ borderRadius: 1 }}
              />
            </Stack>
          </Stack>
          <Alert severity="info" variant="outlined">
            These figures are estimates only. They may change during
            verification or payroll review.
          </Alert>
          <Box sx={{ height: 360 }}>
            <DataGrid
              rows={pendingPay?.lines ?? []}
              getRowId={(row) => row.shift}
              disableRowSelectionOnClick
              getRowClassName={pendingOvertimeRowClassName}
              sx={overtimePendingRowSx}
              columns={[
                {
                  field: 'start',
                  headerName: 'Shift',
                  minWidth: 170,
                  valueGetter: (value) =>
                    moment(value).format('DD MMM YYYY HH:mm'),
                },
                {
                  field: 'description',
                  headerName: 'Description',
                  minWidth: 190,
                  flex: 1,
                },
                {
                  field: 'clinicalRole',
                  headerName: 'Role',
                  minWidth: 130,
                },
                {
                  field: 'status',
                  headerName: 'Status',
                  minWidth: 170,
                  renderCell: ({ row }) => (
                    <Stack
                      direction="row"
                      gap={0.5}
                      flexWrap="wrap"
                      alignItems="center"
                      height="100%"
                    >
                      <Chip
                        size="small"
                        label={
                          row.payRun?.reference
                            ? `${row.status} ${row.payRun.reference}`
                            : row.status
                        }
                        color={
                          row.status === 'Ready for Payroll'
                            ? 'success'
                            : row.status === 'In Pay Run'
                              ? 'info'
                              : 'warning'
                        }
                        variant="outlined"
                        sx={{ borderRadius: 1 }}
                      />
                      {row?.clock?.legacyIncomplete && !row.isVerified && (
                        <Chip
                          size="small"
                          label="Legacy clock data"
                          variant="outlined"
                          sx={{ borderRadius: 1 }}
                        />
                      )}
                    </Stack>
                  ),
                },
                {
                  field: 'overtime',
                  headerName: 'Overtime',
                  minWidth: 200,
                  sortable: false,
                  renderCell: ({ row }) => (
                    <OvertimeChip
                      overtime={row.overtime}
                      format={currency.format}
                    />
                  ),
                },
                ...(workerPaymentType === 'Invoice'
                  ? [
                      {
                        field: 'staffInvoice',
                        headerName: 'Invoice',
                        minWidth: 150,
                        renderCell: ({ row }) =>
                          row.staffInvoice ? (
                            <Chip
                              size="small"
                              label={row.staffInvoice.status}
                              color={
                                row.staffInvoice.status === 'Approved' ||
                                row.staffInvoice.status === 'Matched'
                                  ? 'success'
                                  : row.staffInvoice.status === 'Rejected'
                                    ? 'error'
                                    : 'warning'
                              }
                              variant="outlined"
                              sx={{ borderRadius: 1 }}
                            />
                          ) : (
                            <Chip
                              size="small"
                              label="Not uploaded"
                              variant="outlined"
                              sx={{ borderRadius: 1, cursor: 'pointer' }}
                              onClick={() => setUploadInvoiceOpen(true)}
                            />
                          ),
                      },
                    ]
                  : []),
                {
                  field: 'hours',
                  headerName: 'Hours',
                  minWidth: 90,
                },
                {
                  field: 'basePay',
                  headerName: 'Base',
                  minWidth: 110,
                  valueGetter: (value) => currency.format(value ?? 0),
                },
                {
                  field: 'overtimePay',
                  headerName: 'Overtime',
                  minWidth: 120,
                  valueGetter: (value) => currency.format(value ?? 0),
                },
                {
                  field: 'bonusPay',
                  headerName: 'Bonus',
                  minWidth: 110,
                  valueGetter: (value) => currency.format(value ?? 0),
                },
                {
                  field: 'travelPay',
                  headerName: 'Travel',
                  minWidth: 110,
                  valueGetter: (value) => currency.format(value ?? 0),
                },
                {
                  field: 'totalPay',
                  headerName: 'Estimate',
                  minWidth: 120,
                  valueGetter: (value) => currency.format(value ?? 0),
                },
              ]}
              slots={{
                noRowsOverlay: () => (
                  <Stack
                    alignItems="center"
                    justifyContent="center"
                    height="100%"
                  >
                    <Alert severity="success">
                      No completed unpaid shifts.
                    </Alert>
                  </Stack>
                ),
              }}
            />
          </Box>
        </Stack>
      </Paper>

      {workerPaymentType === 'Invoice' && (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack gap={2}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              gap={1}
            >
              <Stack>
                <Typography variant="title2">Uploaded Invoices</Typography>
                <Typography variant="body2" color="text.secondary">
                  Invoices submitted against completed unpaid shifts.
                </Typography>
              </Stack>
            </Stack>
            <Box sx={{ height: 320 }}>
              <DataGrid
                rows={staffInvoices}
                getRowId={(row) => row._id}
                disableRowSelectionOnClick
                columns={[
                  {
                    field: 'invoiceNumber',
                    headerName: 'Invoice',
                    minWidth: 150,
                    valueGetter: (value) => value || 'Not supplied',
                  },
                  {
                    field: 'invoiceDate',
                    headerName: 'Date',
                    minWidth: 130,
                    valueGetter: (value) =>
                      value ? moment(value).format('DD MMM YYYY') : '',
                  },
                  {
                    field: 'amount',
                    headerName: 'Amount',
                    minWidth: 120,
                    valueGetter: (value) => currency.format(value ?? 0),
                  },
                  {
                    field: 'expectedAmount',
                    headerName: 'Expected',
                    minWidth: 120,
                    valueGetter: (value) => currency.format(value ?? 0),
                  },
                  {
                    field: 'generatedByPlatform',
                    headerName: 'Source',
                    minWidth: 110,
                    renderCell: ({ row }) => (
                      <Chip
                        size="small"
                        label={
                          row.generatedByPlatform ? 'Generated' : 'Uploaded'
                        }
                        variant="outlined"
                        sx={{ borderRadius: 1 }}
                      />
                    ),
                  },
                  {
                    field: 'status',
                    headerName: 'Status',
                    minWidth: 120,
                    renderCell: ({ row }) => (
                      <Chip
                        size="small"
                        label={row.status}
                        color={
                          row.status === 'Approved' || row.status === 'Matched'
                            ? 'success'
                            : row.status === 'Rejected'
                              ? 'error'
                              : 'warning'
                        }
                        variant="outlined"
                        sx={{ borderRadius: 1 }}
                      />
                    ),
                  },
                  {
                    field: 'shifts',
                    headerName: 'Shifts',
                    minWidth: 110,
                    valueGetter: (value, row) =>
                      `${value?.length ?? 0}${
                        row.overtimeShifts?.length
                          ? ` + ${row.overtimeShifts.length} OT`
                          : ''
                      }`,
                  },
                  {
                    field: 'file',
                    headerName: '',
                    minWidth: 120,
                    sortable: false,
                    renderCell: ({ row }) => (
                      <Button
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={() => openPreview(row.file)}
                        disabled={!row.file?.key}
                      >
                        View
                      </Button>
                    ),
                  },
                ]}
                slots={{
                  noRowsOverlay: () => (
                    <Stack
                      alignItems="center"
                      justifyContent="center"
                      height="100%"
                    >
                      <Alert severity="info">No invoices uploaded yet.</Alert>
                    </Stack>
                  ),
                }}
              />
            </Box>
          </Stack>
        </Paper>
      )}

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Stack gap={2}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            gap={1}
          >
            <Stack>
              <Typography variant="title2">Expenses</Typography>
              <Typography variant="body2" color="text.secondary">
                Log an expense and send it for approval.
              </Typography>
            </Stack>
            <Button
              startIcon={<AddCircleOutlineIcon />}
              variant="contained"
              onClick={() => {
                setExpenseError(null)
                setLogExpenseOpen(true)
              }}
              sx={{ alignSelf: { xs: 'stretch', md: 'center' } }}
            >
              Log Expense
            </Button>
          </Stack>
          <Box sx={{ height: 320 }}>
            <DataGrid
              rows={expenses}
              getRowId={(row) => row._id}
              disableRowSelectionOnClick
              columns={[
                {
                  field: 'expenseDate',
                  headerName: 'Date',
                  minWidth: 130,
                  valueGetter: (value) =>
                    value ? moment(value).format('DD MMM YYYY') : '',
                },
                {
                  field: 'description',
                  headerName: 'Description',
                  minWidth: 190,
                  flex: 1,
                },
                {
                  field: 'category',
                  headerName: 'Category',
                  minWidth: 130,
                },
                {
                  field: 'amount',
                  headerName: 'Amount',
                  minWidth: 110,
                  valueGetter: (value) => currency.format(value ?? 0),
                },
                {
                  field: 'shift',
                  headerName: 'Linked Shift',
                  minWidth: 130,
                  valueGetter: (value) => (value ? 'Yes' : 'No'),
                },
                {
                  field: 'status',
                  headerName: 'Status',
                  minWidth: 120,
                  renderCell: ({ row }) => (
                    <Chip
                      size="small"
                      label={row.status}
                      color={
                        row.status === 'Approved'
                          ? 'success'
                          : row.status === 'Rejected'
                            ? 'error'
                            : 'warning'
                      }
                      variant="outlined"
                      sx={{ borderRadius: 1 }}
                    />
                  ),
                },
                {
                  field: 'receipt',
                  headerName: '',
                  minWidth: 120,
                  sortable: false,
                  renderCell: ({ row }) => (
                    <Button
                      size="small"
                      startIcon={<VisibilityIcon />}
                      onClick={() => openPreview(row.receipt)}
                      disabled={!row.receipt?.key}
                    >
                      View
                    </Button>
                  ),
                },
              ]}
              slots={{
                noRowsOverlay: () => (
                  <Stack
                    alignItems="center"
                    justifyContent="center"
                    height="100%"
                  >
                    <Alert severity="info">No expenses logged yet.</Alert>
                  </Stack>
                ),
              }}
            />
          </Box>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Stack gap={2}>
          <Stack direction="row" justifyContent="space-between" gap={1}>
            <Typography variant="title2">Statements</Typography>
            {loading && (
              <LinearProgress sx={{ width: 160, alignSelf: 'center' }} />
            )}
          </Stack>
          <Box sx={{ height: 420 }}>
            <DataGrid
              rows={statements}
              getRowId={(row) => row._id}
              disableRowSelectionOnClick
              onRowClick={({ row }) => setSelectedStatement(row)}
              columns={[
                {
                  field: 'reference',
                  headerName: 'Reference',
                  minWidth: 170,
                  flex: 1,
                },
                {
                  field: 'statementType',
                  headerName: 'Type',
                  minWidth: 170,
                  renderCell: ({ row }) => (
                    <Chip
                      size="small"
                      label={row.statementType}
                      color={
                        row.statementType === 'Remittance Summary'
                          ? 'info'
                          : 'success'
                      }
                    />
                  ),
                },
                {
                  field: 'period',
                  headerName: 'Period',
                  minWidth: 220,
                  valueGetter: (value, row) =>
                    `${moment(row.periodStart).format('DD MMM YYYY')} - ${moment(
                      row.periodEnd,
                    ).format('DD MMM YYYY')}`,
                },
                {
                  field: 'paidAt',
                  headerName: 'Paid',
                  minWidth: 150,
                  valueGetter: (value) =>
                    value ? moment(value).format('DD MMM YYYY') : '',
                },
                {
                  field: 'shifts',
                  headerName: 'Shifts',
                  minWidth: 90,
                  valueGetter: (value, row) => row?.employeeTotals?.shifts ?? 0,
                },
                {
                  field: 'total',
                  headerName: 'Total',
                  minWidth: 130,
                  valueGetter: (value, row) =>
                    currency.format(row?.employeeTotals?.totalPay ?? 0),
                },
              ]}
              slots={{
                noRowsOverlay: () => (
                  <Stack
                    alignItems="center"
                    justifyContent="center"
                    height="100%"
                  >
                    <Alert severity="info">No paid statements yet.</Alert>
                  </Stack>
                ),
              }}
            />
          </Box>
        </Stack>
      </Paper>

      <Dialog
        open={!!selectedStatement}
        onClose={() => setSelectedStatement(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{selectedStatement?.reference}</DialogTitle>
        <DialogContent dividers>
          {selectedStatement && (
            <Stack gap={2}>
              <Stack direction="row" gap={1} flexWrap="wrap">
                <Chip label={selectedStatement.statementType} />
                <Chip
                  label={`${selectedStatement?.employeeTotals?.shifts ?? 0} shifts`}
                />
                <Chip
                  label={`${selectedStatement?.employeeTotals?.hours ?? 0} hours`}
                />
                <Chip
                  label={currency.format(
                    selectedStatement?.employeeTotals?.totalPay ?? 0,
                  )}
                />
              </Stack>
              <Box sx={{ height: 420 }}>
                <DataGrid
                  rows={selectedStatement.lines ?? []}
                  getRowId={(row) => row.shift}
                  disableRowSelectionOnClick
                  columns={[
                    {
                      field: 'start',
                      headerName: 'Shift',
                      minWidth: 170,
                      valueGetter: (value) =>
                        moment(value).format('DD MMM YYYY HH:mm'),
                    },
                    {
                      field: 'description',
                      headerName: 'Description',
                      minWidth: 190,
                      flex: 1,
                    },
                    {
                      field: 'clinicalRole',
                      headerName: 'Role',
                      minWidth: 130,
                    },
                    {
                      field: 'hours',
                      headerName: 'Hours',
                      minWidth: 90,
                    },
                    {
                      field: 'payRate',
                      headerName: 'Rate',
                      minWidth: 100,
                      valueGetter: (value) => currency.format(value ?? 0),
                    },
                    {
                      field: 'bonusPay',
                      headerName: 'Bonus',
                      minWidth: 100,
                      valueGetter: (value) => currency.format(value ?? 0),
                    },
                    {
                      field: 'travelPay',
                      headerName: 'Travel',
                      minWidth: 100,
                      valueGetter: (value) => currency.format(value ?? 0),
                    },
                    {
                      field: 'totalPay',
                      headerName: 'Total',
                      minWidth: 120,
                      valueGetter: (value) => currency.format(value ?? 0),
                    },
                  ]}
                />
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedStatement(null)}>Close</Button>
          <Button
            startIcon={<DownloadIcon />}
            variant="contained"
            onClick={() => downloadStatement(selectedStatement)}
          >
            Download CSV
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={uploadInvoiceOpen}
        onClose={closeUploadInvoice}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Upload Staff Invoice</DialogTitle>
        <DialogContent dividers>
          <Stack gap={2}>
            <Alert severity="info" variant="outlined">
              Select completed unpaid shifts that this invoice relates to.
              Uploading an invoice does not mark shifts as paid.
            </Alert>
            {invoiceError && (
              <Alert severity="error" onClose={() => setInvoiceError(null)}>
                {invoiceError}
              </Alert>
            )}
            <Stack direction={{ xs: 'column', md: 'row' }} gap={1}>
              <TextField
                label="Invoice Number"
                value={invoiceForm.invoiceNumber}
                onChange={(e) =>
                  setInvoiceForm((prev) => ({
                    ...prev,
                    invoiceNumber: e.target.value,
                  }))
                }
                fullWidth
              />
              <TextField
                label="Invoice Date"
                type="date"
                value={invoiceForm.invoiceDate}
                onChange={(e) =>
                  setInvoiceForm((prev) => ({
                    ...prev,
                    invoiceDate: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Invoice Amount"
                type="number"
                value={invoiceForm.amount}
                onChange={(e) =>
                  setInvoiceForm((prev) => ({
                    ...prev,
                    amount: e.target.value,
                  }))
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">{getCurrencySymbol()}</InputAdornment>
                  ),
                }}
                inputProps={{ min: 0, step: 0.01 }}
                helperText={
                  vatRate
                    ? `Must match the selected total including VAT: ${currency.format(selectedInvoiceTotals.net)} net + ${currency.format(selectedInvoiceTotals.vat)} VAT = ${currency.format(selectedInvoiceTotals.gross)}`
                    : 'Must match the selected total'
                }
                fullWidth
                required
              />
            </Stack>
            <DocumentsField
              files={invoiceFiles}
              onFilesChange={setInvoiceFiles}
              single
              accept="application/pdf,image/*"
              caption="Optional — a PDF or photo of your invoice"
              acceptLabel="PDF or image files"
              pendingLabel="Uploads when you submit"
              disabled={uploadingInvoice}
            />
            {invoiceAmountMismatch && (
              <Alert severity="error" variant="outlined">
                Invoice amount {currency.format(invoiceAmount)} must match the
                selected total {currency.format(selectedInvoiceTotals.gross)},
                which includes any approved overtime you have ticked
                {vatRate ? ' and VAT' : ''}.
              </Alert>
            )}
            <TextField
              label="Notes"
              value={invoiceForm.notes}
              onChange={(e) =>
                setInvoiceForm((prev) => ({
                  ...prev,
                  notes: e.target.value,
                }))
              }
              multiline
              minRows={2}
              fullWidth
            />
            <Stack direction="row" gap={1} flexWrap="wrap">
              <Chip label={selectionLabel(invoiceSelection)} />
              {vatRate ? (
                <>
                  <Chip
                    variant="outlined"
                    label={`Net ${currency.format(selectedInvoiceTotals.net)}`}
                  />
                  <Chip
                    variant="outlined"
                    label={`VAT @ ${Math.round(vatRate * 100)}% ${currency.format(selectedInvoiceTotals.vat)}`}
                  />
                  <Chip
                    color="warning"
                    label={`Expected ${currency.format(selectedInvoiceTotals.gross)}`}
                  />
                </>
              ) : (
                <Chip
                  color="warning"
                  label={`Expected ${currency.format(selectedInvoiceTotals.gross)}`}
                />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Overtime claims are listed on their own rows. Approved overtime
              can be ticked with its shift; a claim still awaiting a decision
              can't be invoiced yet. Click a row to see its details.
            </Typography>
            <Box sx={{ height: 360 }}>
              <DataGrid
                rows={invoiceRows}
                checkboxSelection
                disableRowSelectionOnClick
                rowSelectionModel={invoiceSelection}
                onRowSelectionModelChange={changeInvoiceSelection}
                isRowSelectable={({ row }) =>
                  isInvoiceRowSelectable(row, invoiceSelection)
                }
                getRowClassName={invoiceRowClassName}
                onRowClick={({ row }) => setViewedInvoiceRow(row)}
                sx={invoiceGridSx}
                columns={invoiceRowColumns(currency.format)}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeUploadInvoice}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<UploadFileIcon />}
            onClick={uploadStaffInvoice}
            disabled={
              uploadingInvoice ||
              !invoiceSelection.length ||
              !hasInvoiceAmount ||
              invoiceAmountMismatch
            }
          >
            {uploadingInvoice ? 'Uploading...' : 'Submit Invoice'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={generateInvoiceOpen}
        onClose={() => {
          setGenerateInvoiceOpen(false)
          setGenerateError(null)
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Generate Invoice</DialogTitle>
        <DialogContent dividers>
          <Stack gap={2}>
            <Alert severity="info" variant="outlined">
              Select completed unpaid shifts to generate an invoice PDF for. The
              invoice amount is calculated automatically from the selected
              shifts.
            </Alert>
            {generateError && (
              <Alert severity="error" onClose={() => setGenerateError(null)}>
                {generateError}
              </Alert>
            )}
            <Stack direction={{ xs: 'column', md: 'row' }} gap={1}>
              <TextField
                label="Invoice Number"
                value={generateForm.invoiceNumber}
                onChange={(e) =>
                  setGenerateForm((prev) => ({
                    ...prev,
                    invoiceNumber: e.target.value,
                  }))
                }
                helperText="Optional - leave blank to auto-generate"
                fullWidth
              />
              <TextField
                label="Invoice Date"
                type="date"
                value={generateForm.invoiceDate}
                onChange={(e) =>
                  setGenerateForm((prev) => ({
                    ...prev,
                    invoiceDate: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Stack>
            <TextField
              label="Notes"
              value={generateForm.notes}
              onChange={(e) =>
                setGenerateForm((prev) => ({
                  ...prev,
                  notes: e.target.value,
                }))
              }
              multiline
              minRows={2}
              fullWidth
            />
            <Stack direction="row" gap={1} flexWrap="wrap">
              <Chip label={selectionLabel(generateSelection)} />
              {vatRate ? (
                <>
                  <Chip
                    variant="outlined"
                    label={`Net ${currency.format(selectedGenerateTotals.net)}`}
                  />
                  <Chip
                    variant="outlined"
                    label={`VAT @ ${Math.round(vatRate * 100)}% ${currency.format(selectedGenerateTotals.vat)}`}
                  />
                  <Chip
                    color="warning"
                    label={`Total ${currency.format(selectedGenerateTotals.gross)}`}
                  />
                </>
              ) : (
                <Chip
                  color="warning"
                  label={`Total ${currency.format(selectedGenerateTotals.gross)}`}
                />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Overtime claims are listed on their own rows. Approved overtime
              can be ticked with its shift; a claim still awaiting a decision
              can't be invoiced yet. Click a row to see its details.
            </Typography>
            <Box sx={{ height: 360 }}>
              <DataGrid
                rows={invoiceRows}
                checkboxSelection
                disableRowSelectionOnClick
                rowSelectionModel={generateSelection}
                onRowSelectionModelChange={changeGenerateSelection}
                isRowSelectable={({ row }) =>
                  isInvoiceRowSelectable(row, generateSelection)
                }
                getRowClassName={invoiceRowClassName}
                onRowClick={({ row }) => setViewedInvoiceRow(row)}
                sx={invoiceGridSx}
                columns={invoiceRowColumns(currency.format)}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setGenerateInvoiceOpen(false)
              setGenerateError(null)
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<ReceiptLongIcon />}
            onClick={generateStaffInvoice}
            disabled={generatingInvoice || !generateSelection.length}
          >
            {generatingInvoice ? 'Generating...' : 'Generate Invoice'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={logExpenseOpen}
        onClose={() => setLogExpenseOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Log Expense</DialogTitle>
        <DialogContent dividers>
          <Stack gap={2}>
            <TextField
              label="Description"
              value={expenseForm.description}
              onChange={(e) =>
                setExpenseForm((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              fullWidth
              required
            />
            <Stack direction={{ xs: 'column', md: 'row' }} gap={1}>
              <TextField
                select
                label="Category"
                value={expenseForm.category}
                onChange={(e) =>
                  setExpenseForm((prev) => ({
                    ...prev,
                    category: e.target.value,
                  }))
                }
                fullWidth
              >
                {expenseCategories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Amount"
                type="number"
                value={expenseForm.amount}
                onChange={(e) =>
                  setExpenseForm((prev) => ({
                    ...prev,
                    amount: e.target.value,
                  }))
                }
                fullWidth
                required
              />
              <TextField
                label="Expense Date"
                type="date"
                value={expenseForm.expenseDate}
                onChange={(e) =>
                  setExpenseForm((prev) => ({
                    ...prev,
                    expenseDate: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Stack>
            <Autocomplete
              options={recentShiftOptions}
              value={expenseForm.shift}
              getOptionLabel={(option) =>
                option
                  ? `${moment(option.start).format('DD MMM YYYY')} - ${option.description ?? ''}`
                  : ''
              }
              isOptionEqualToValue={(option, value) =>
                option.shift === value?.shift
              }
              renderOption={(props, option) => (
                <li {...props} key={option.shift}>
                  {`${moment(option.start).format('DD MMM YYYY')} - ${option.description ?? ''}`}
                </li>
              )}
              onChange={(e, newValue) =>
                setExpenseForm((prev) => ({ ...prev, shift: newValue }))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Link to a recent shift (optional)"
                />
              )}
            />
            <TextField
              type="file"
              inputProps={{ accept: 'application/pdf,image/png,image/jpeg' }}
              onChange={(e) =>
                setExpenseForm((prev) => ({
                  ...prev,
                  receipt: e.target.files?.[0] ?? null,
                }))
              }
              helperText="Optional receipt (PDF, PNG, or JPG)."
            />
            <TextField
              label="Notes"
              value={expenseForm.notes}
              onChange={(e) =>
                setExpenseForm((prev) => ({ ...prev, notes: e.target.value }))
              }
              multiline
              minRows={2}
              fullWidth
            />
            {expenseError && (
              <Alert severity="error" variant="outlined">
                {expenseError}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogExpenseOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={submitExpense}
            disabled={
              submittingExpense ||
              !expenseForm.description.trim() ||
              expenseForm.amount === '' ||
              !Number.isFinite(Number(expenseForm.amount))
            }
          >
            {submittingExpense ? 'Submitting...' : 'Submit Expense'}
          </Button>
        </DialogActions>
      </Dialog>

      {previewModal}
      <InvoiceRowDialog
        row={viewedInvoiceRow}
        onClose={() => setViewedInvoiceRow(null)}
        format={currency.format}
      />
    </Stack>
  )
}

export default MyPay
